/**
 * Composable for managing Christmas/New Year promo modal display
 *
 * Features:
 * - Automatic date range detection (UTC+0)
 * - 12-hour throttle to prevent showing too frequently
 * - Mode selection (Christmas vs New Year)
 * - Not shown on demo environment
 */

import { useRuntimeConfig } from 'nuxt/app';

export type PromoModalMode =
  | 'christmas'
  | 'new-year'
  | 'chinese-new-year'
  | 'spring'
  | 'st-patrick'
  | 'easter'
  | 'golden-week';

interface PromoModalState {
  shouldShow: ComputedRef<boolean>;
  mode: ComputedRef<PromoModalMode | null>;
  markAsShown: () => void;
}

const STORAGE_KEY = 'christmas-promo-last-shown';
const THROTTLE_HOURS = 12;

/**
 * Check if current date (UTC+0) is within promo period
 * @returns Object with shouldShow flag and mode
 */
export default function usePromoModal(): PromoModalState {
  // Only run on client-side to avoid SSR issues
  if (import.meta.server) {
    const shouldShowComputed = computed(() => false);
    const modeComputed = computed(() => null as PromoModalMode | null);
    return {
      shouldShow: shouldShowComputed,
      mode: modeComputed,
      markAsShown: () => {}
    };
  }

  const shouldShow = ref(false);
  const mode = ref<PromoModalMode | null>(null);
  const config = useRuntimeConfig();

  const checkPromoStatus = () => {
    if (config.public.env === 'demo') {
      shouldShow.value = false;
      mode.value = null;
      return;
    }
    const now = new Date();
    const currentYear = now.getUTCFullYear();

    // Get current UTC date components
    const currentMonth = now.getUTCMonth(); // 0-11 (0 = January)
    const currentDate = now.getUTCDate(); // 1-31

    const isJanuary = currentMonth === 0; // January
    const yearForDec = isJanuary ? currentYear - 1 : currentYear;
    const yearForJan = isJanuary ? currentYear : currentYear + 1;

    const christmasStart = new Date(Date.UTC(yearForDec, 11, 24, 0, 0, 0, 0));
    const christmasEnd = new Date(Date.UTC(yearForDec, 11, 29, 23, 59, 59, 999));

    const newYearStart = new Date(Date.UTC(yearForDec, 11, 30, 0, 0, 0, 0));
    const newYearEnd = new Date(Date.UTC(yearForJan, 0, 4, 23, 59, 59, 999));

    // Chinese New Year: whole February (UTC), ends March 1
    const lastDayFeb = new Date(Date.UTC(currentYear, 2, 0)).getUTCDate();
    const chineseNewYearStart = new Date(Date.UTC(currentYear, 1, 1, 0, 0, 0, 0));
    const chineseNewYearEnd = new Date(Date.UTC(currentYear, 1, lastDayFeb, 23, 59, 59, 999));

    // Spring sales: March 5–15 inclusive (UTC)
    const springStart = new Date(Date.UTC(currentYear, 2, 5, 0, 0, 0, 0));
    const springEnd = new Date(Date.UTC(currentYear, 2, 15, 23, 59, 59, 999));

    // St. Patrick: March 16–18 inclusive (UTC)
    const stPatrickStart = new Date(Date.UTC(currentYear, 2, 16, 0, 0, 0, 0));
    const stPatrickEnd = new Date(Date.UTC(currentYear, 2, 18, 23, 59, 59, 999));

    // Easter: April 3–6 inclusive (UTC)
    const easterStart = new Date(Date.UTC(currentYear, 3, 3, 0, 0, 0, 0));
    const easterEnd = new Date(Date.UTC(currentYear, 3, 6, 23, 59, 59, 999));

    // Golden Week: April 29 – May 6 inclusive (UTC)
    const goldenWeekStart = new Date(Date.UTC(currentYear, 3, 29, 0, 0, 0, 0));
    const goldenWeekEnd = new Date(Date.UTC(currentYear, 4, 6, 23, 59, 59, 999));

    const nowUTC = new Date(
      Date.UTC(currentYear, currentMonth, currentDate, now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds())
    );

    // Check if we're in a promo period
    let isInPromoPeriod = false;
    let promoMode: PromoModalMode | null = null;

    if (nowUTC >= christmasStart && nowUTC <= christmasEnd) {
      isInPromoPeriod = true;
      promoMode = 'christmas';
    } else if (nowUTC >= newYearStart && nowUTC <= newYearEnd) {
      isInPromoPeriod = true;
      promoMode = 'new-year';
    } else if (nowUTC >= chineseNewYearStart && nowUTC <= chineseNewYearEnd) {
      isInPromoPeriod = true;
      promoMode = 'chinese-new-year';
    } else if (nowUTC >= springStart && nowUTC <= springEnd) {
      isInPromoPeriod = true;
      promoMode = 'spring';
    } else if (nowUTC >= stPatrickStart && nowUTC <= stPatrickEnd) {
      isInPromoPeriod = true;
      promoMode = 'st-patrick';
    } else if (nowUTC >= easterStart && nowUTC <= easterEnd) {
      isInPromoPeriod = true;
      promoMode = 'easter';
    } else if (nowUTC >= goldenWeekStart && nowUTC <= goldenWeekEnd) {
      isInPromoPeriod = true;
      promoMode = 'golden-week';
    }

    if (!isInPromoPeriod) {
      shouldShow.value = false;
      mode.value = null;
      return;
    }

    // Check throttle (12-hour limit)
    const lastShownStr = localStorage.getItem(STORAGE_KEY);
    if (lastShownStr) {
      const lastShown = parseInt(lastShownStr, 10);
      const hoursSinceLastShow = (nowUTC.getTime() - lastShown) / (1000 * 60 * 60);

      if (hoursSinceLastShow < THROTTLE_HOURS) {
        shouldShow.value = false;
        mode.value = promoMode; // Keep mode for reference, but don't show
        return;
      }
    }

    // All checks passed - show the modal
    shouldShow.value = true;
    mode.value = promoMode;
  };

  let interval: ReturnType<typeof setInterval> | null = null;

  // Check on mount and set up interval for date transitions
  onMounted(() => {
    if (import.meta.client) {
      checkPromoStatus();

      // Re-check every hour to catch date transitions
      interval = setInterval(
        () => {
          checkPromoStatus();
        },
        60 * 60 * 1000
      ); // 1 hour
    }
  });

  onBeforeUnmount(() => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  });

  /**
   * Mark modal as shown (call this when modal is displayed)
   */
  const markAsShown = () => {
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      shouldShow.value = false;
    }
  };

  const shouldShowComputed: ComputedRef<boolean> = computed(() => shouldShow.value);
  const modeComputed: ComputedRef<PromoModalMode | null> = computed(() => mode.value);

  return {
    shouldShow: shouldShowComputed,
    mode: modeComputed,
    markAsShown
  } as PromoModalState;
}
